import { memo, useState } from 'react'
import { Handle, Position, useReactFlow, type Node, type NodeProps } from '@xyflow/react'
import { ChevronDown, ChevronRight, Link2, Minus, Plus, X } from 'lucide-react'
import type { GraphNodeData, ScalarType } from '../../types'
import { headerBg, headerText } from '../../lib/colorFromKey'
import { useGraphStore } from '../../stores/useGraphStore'

export type ObjectRFNode = Node<GraphNodeData, 'objectNode'>

function ObjectNodeInner({ id, data }: NodeProps<ObjectRFNode>) {
  if (data.kind === 'value') return <ValueCard id={id} data={data} />
  return <ContainerCard id={id} data={data} />
}

function ValueCard({ id, data }: { id: string; data: GraphNodeData }) {
  const updateScalar = useGraphStore((s) => s.updateScalarValue)
  const hBg = headerBg(data.hue)
  const hText = headerText(data.hue)

  return (
    <div className="bg-background border border-border rounded-md shadow-none text-[13px] w-[440px]">
      <Handle
        type="target"
        position={Position.Left}
        id="in"
        style={{
          background: 'transparent',
          border: '1px solid hsl(0 0% 80%)',
          width: 8,
          height: 8,
          left: -4,
        }}
      />
      <div
        className="px-3 py-2 rounded-t-md select-none"
        style={{ backgroundColor: hBg, color: hText }}
      >
        <span
          className="font-medium tracking-tight"
          style={{ fontFamily: 'Atlas Grotesk, system-ui, sans-serif' }}
        >
          {data.label}
        </span>
      </div>
      <div className="px-3 py-2">
        <InlineValue
          value={data.scalarValue ?? null}
          valueType={data.scalarType ?? 'null'}
          onCommit={(nv) => updateScalar(id, nv)}
        />
      </div>
      <Handle
        type="source"
        position={Position.Right}
        id={data.outputPortId}
        style={{
          background: 'transparent',
          border: '1px dashed hsl(0 0% 80%)',
          width: 8,
          height: 8,
          right: -4,
          top: 16,
        }}
      />
    </div>
  )
}

function ContainerCard({ id, data }: { id: string; data: GraphNodeData }) {
  const updateInlineField = useGraphStore((s) => s.updateInlineField)
  const renameInlineKey = useGraphStore((s) => s.renameInlineKey)
  const addInlineField = useGraphStore((s) => s.addInlineField)
  const removeField = useGraphStore((s) => s.removeField)
  const toggleCollapsed = useGraphStore((s) => s.toggleCollapsed)
  const toggleHideDescendants = useGraphStore((s) => s.toggleHideDescendants)
  const togglePointerCollapsed = useGraphStore((s) => s.togglePointerCollapsed)
  const flow = useReactFlow()

  const [adding, setAdding] = useState(false)
  const [newKey, setNewKey] = useState('')

  const jumpTo = (nodeId: string | null) => {
    if (!nodeId) return
    const node = flow.getNode(nodeId)
    if (!node) return
    flow.setCenter(node.position.x + 160, node.position.y + 80, { zoom: 1, duration: 400 })
  }

  const hBg = headerBg(data.hue)
  const hText = headerText(data.hue)
  const rowCount = data.inlineFields.length + data.pointerFields.length

  return (
    <div className="bg-background border border-border rounded-md shadow-none text-[13px] w-[440px]">
      <Handle
        type="target"
        position={Position.Left}
        id="in"
        style={{
          background: 'transparent',
          border: '1px solid hsl(0 0% 80%)',
          width: 8,
          height: 8,
          left: -4,
        }}
      />

      <div
        className="flex items-center justify-between px-3 py-2 rounded-t-md select-none"
        style={{ backgroundColor: hBg, color: hText }}
      >
        <button
          onClick={() => toggleCollapsed(id)}
          className="flex items-center gap-1.5 font-medium cursor-pointer min-w-0"
          style={{ fontFamily: 'Atlas Grotesk, system-ui, sans-serif', color: hText }}
          title={data.collapsed ? 'Expand body' : 'Collapse body'}
        >
          {data.collapsed ? (
            <ChevronRight className="w-3.5 h-3.5" strokeWidth={1.5} />
          ) : (
            <ChevronDown className="w-3.5 h-3.5" strokeWidth={1.5} />
          )}
          <span className="tracking-tight truncate">{data.label || 'root'}</span>
          <span className="text-[10px] uppercase tracking-wider opacity-60 ml-1">{data.kind}</span>
        </button>
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-wider opacity-60">
            {data.kind === 'array'
              ? `${rowCount === 1 ? '1 item' : `${data.pointerFields[0]?.childIds.length ?? 0} items`}`
              : `${rowCount} ${rowCount === 1 ? 'key' : 'keys'}`}
          </span>
          {data.pointerFields.some((p) => !p.ref && p.childIds.length > 0) && (
            <button
              onClick={(e) => {
                e.stopPropagation()
                toggleHideDescendants(id)
              }}
              className="p-0.5 -m-0.5 opacity-60 hover:opacity-100"
              style={{ color: hText }}
              title={data.hideDescendants ? 'Show connections' : 'Hide connections'}
            >
              {data.hideDescendants ? (
                <Plus className="w-3.5 h-3.5" strokeWidth={1.5} />
              ) : (
                <Minus className="w-3.5 h-3.5" strokeWidth={1.5} />
              )}
            </button>
          )}
        </div>
      </div>

      {!data.collapsed && (
        <div className="py-1">
          {data.inlineFields.map((f) => {
            const isRef = Boolean(f.ref)
            return (
              <div
                key={f.key}
                data-field-key={f.key}
                className={`group relative flex items-start gap-2 px-3 py-1.5 hover:bg-muted/30 ${
                  isRef ? 'border-l-2 border-dashed border-foreground/20' : ''
                }`}
                title={isRef ? `Reference → ${f.ref!.pointer}` : undefined}
              >
                <span className="flex items-start">
                  <InlineKey
                    value={f.key}
                    editable={!isRef}
                    onCommit={(nk) => renameInlineKey(id, f.key, nk)}
                  />
                  <span className="text-muted-foreground">:</span>
                </span>
                {isRef ? (
                  <button
                    onClick={() => jumpTo(f.ref!.sourceNodeId)}
                    className="flex-1 flex items-center gap-1.5 text-left italic text-muted-foreground truncate hover:text-foreground"
                    style={{ fontFamily: 'Frame Text, Georgia, serif' }}
                  >
                    <Link2 className="w-3 h-3 flex-shrink-0" strokeWidth={1.5} />
                    <span className="truncate">
                      {f.value === null || f.value === undefined
                        ? f.ref!.pointer
                        : String(f.value)}
                    </span>
                  </button>
                ) : (
                  <InlineValue
                    value={f.value}
                    valueType={f.valueType}
                    onCommit={(nv) => updateInlineField(id, f.key, nv)}
                  />
                )}
                <button
                  className="ml-auto opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive p-0.5 -m-0.5"
                  onClick={() => removeField(id, f.key)}
                  title="Remove"
                >
                  <X className="w-3 h-3" strokeWidth={1.5} />
                </button>
              </div>
            )
          })}

          {data.pointerFields.map((p) => {
            const isRef = Boolean(p.ref)
            return (
              <div
                key={p.key}
                data-field-key={p.key}
                className={`group relative flex items-center gap-2 px-3 py-1.5 hover:bg-muted/30 ${
                  isRef ? 'border-l-2 border-dashed border-foreground/20' : ''
                }`}
                title={isRef ? `Reference → ${p.ref!.pointer}` : undefined}
              >
                {!isRef && p.childIds.length > 0 && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      togglePointerCollapsed(id, p.key)
                    }}
                    className="p-0.5 -m-0.5 text-muted-foreground hover:text-foreground"
                    title={p.collapsed ? 'Expand descendants' : 'Collapse descendants'}
                  >
                    {p.collapsed ? (
                      <ChevronRight className="w-3 h-3" strokeWidth={1.5} />
                    ) : (
                      <ChevronDown className="w-3 h-3" strokeWidth={1.5} />
                    )}
                  </button>
                )}
                <span
                  className="font-light"
                  style={{ fontFamily: 'Atlas Grotesk, system-ui, sans-serif' }}
                >
                  {(data.kind === 'array' ? `${data.label}[${p.key}]` : p.key)}
                  <span className="text-muted-foreground">:</span>
                </span>
                {isRef ? (
                  <button
                    onClick={() => jumpTo(p.ref!.sourceNodeId)}
                    className="flex items-center gap-1.5 text-muted-foreground text-[12px] italic hover:text-foreground"
                  >
                    <Link2 className="w-3 h-3 flex-shrink-0" strokeWidth={1.5} />
                    <span className="truncate">{p.summary}</span>
                  </button>
                ) : (
                  <span className="text-muted-foreground text-[12px] italic">{p.summary}</span>
                )}
                <button
                  className="ml-auto opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive p-0.5 -m-0.5"
                  onClick={() => removeField(id, p.key)}
                  title="Remove"
                >
                  <X className="w-3 h-3" strokeWidth={1.5} />
                </button>
                {!isRef && (
                  <Handle
                    type="source"
                    position={Position.Right}
                    id={`ptr:${p.key}`}
                    style={{
                      background: `hsl(${data.hue} 40% 60%)`,
                      border: 'none',
                      width: 6,
                      height: 6,
                      right: -3,
                      top: '50%',
                    }}
                  />
                )}
              </div>
            )
          })}

          {data.kind === 'object' && (
            <div className="px-3 py-1.5 border-t border-border/50">
              {adding ? (
                <form
                  className="flex items-center gap-2"
                  onSubmit={(e) => {
                    e.preventDefault()
                    if (newKey.trim()) {
                      addInlineField(id, newKey.trim())
                      setNewKey('')
                      setAdding(false)
                    }
                  }}
                >
                  <input
                    autoFocus
                    className="flex-1 bg-transparent border-b border-border text-[13px] outline-none focus:border-foreground/40 font-light py-0.5"
                    style={{ fontFamily: 'Atlas Grotesk, system-ui, sans-serif' }}
                    placeholder="new key"
                    value={newKey}
                    onChange={(e) => setNewKey(e.target.value)}
                    onBlur={() => {
                      if (newKey.trim()) addInlineField(id, newKey.trim())
                      setNewKey('')
                      setAdding(false)
                    }}
                  />
                </form>
              ) : (
                <button
                  className="flex items-center gap-1 text-[11px] font-light text-muted-foreground hover:text-foreground uppercase tracking-wider"
                  onClick={() => setAdding(true)}
                >
                  <Plus className="w-3 h-3" strokeWidth={1.5} />
                  Add key
                </button>
              )}
            </div>
          )}
        </div>
      )}

      <Handle
        type="source"
        position={Position.Right}
        id={data.outputPortId}
        style={{
          background: 'transparent',
          border: '1px dashed hsl(0 0% 80%)',
          width: 8,
          height: 8,
          right: -4,
          top: 16,
        }}
      />
    </div>
  )
}

function InlineKey({
  value,
  editable,
  onCommit,
}: {
  value: string
  editable: boolean
  onCommit: (v: string) => void
}) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value)

  if (!editable) {
    return (
      <span
        className="font-light text-muted-foreground"
        style={{ fontFamily: 'Atlas Grotesk, system-ui, sans-serif' }}
      >
        {value}
      </span>
    )
  }

  if (!editing) {
    return (
      <span
        className="font-light cursor-text min-w-[40px]"
        style={{ fontFamily: 'Atlas Grotesk, system-ui, sans-serif' }}
        onDoubleClick={() => {
          setDraft(value)
          setEditing(true)
        }}
      >
        {value}
      </span>
    )
  }
  return (
    <input
      autoFocus
      className="bg-transparent border-b border-foreground/30 text-[13px] outline-none font-light py-0.5 min-w-[40px]"
      style={{ fontFamily: 'Atlas Grotesk, system-ui, sans-serif' }}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => {
        setEditing(false)
        if (draft !== value) onCommit(draft)
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.currentTarget.blur()
        } else if (e.key === 'Escape') {
          setDraft(value)
          setEditing(false)
        }
      }}
    />
  )
}

function InlineValue({
  value,
  valueType,
  onCommit,
}: {
  value: string | number | boolean | null
  valueType: ScalarType
  onCommit: (v: unknown) => void
}) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(() => stringifyScalar(value))

  const color =
    valueType === 'string'
      ? 'text-foreground'
      : valueType === 'number'
        ? 'text-blue-700'
        : valueType === 'boolean'
          ? 'text-amber-700'
          : 'text-muted-foreground'

  if (!editing) {
    return (
      <span
        className={`flex-1 cursor-text whitespace-pre-wrap break-words ${color}`}
        style={{ fontFamily: 'Frame Text, Georgia, serif' }}
        onDoubleClick={() => {
          setDraft(stringifyScalar(value))
          setEditing(true)
        }}
      >
        {renderScalar(value, valueType)}
      </span>
    )
  }
  return (
    <textarea
      autoFocus
      rows={Math.min(8, Math.max(1, draft.split('\n').length))}
      className="flex-1 bg-transparent border-b border-foreground/30 text-[13px] outline-none py-0.5 resize-none whitespace-pre-wrap"
      style={{ fontFamily: 'Frame Text, Georgia, serif' }}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => {
        setEditing(false)
        onCommit(parseScalar(draft, valueType))
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault()
          e.currentTarget.blur()
        } else if (e.key === 'Escape') {
          setDraft(stringifyScalar(value))
          setEditing(false)
        }
      }}
    />
  )
}

function stringifyScalar(v: string | number | boolean | null): string {
  if (v === null) return 'null'
  return String(v)
}

function renderScalar(v: string | number | boolean | null, t: ScalarType) {
  if (t === 'string')
    return v === '' ? <span className="italic text-muted-foreground">empty</span> : String(v)
  if (v === null) return <span className="italic">null</span>
  return String(v)
}

function parseScalar(s: string, type: ScalarType): string | number | boolean | null {
  if (type === 'number') {
    const n = parseFloat(s)
    return Number.isFinite(n) ? n : 0
  }
  if (type === 'boolean') return s === 'true'
  if (type === 'null') return null
  return s
}

export const ObjectNode = memo(ObjectNodeInner)
